import React, { useEffect, useState } from 'react';
import { Copy, Loader2, Plus, Pencil, X, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ADMIN_NAV } from '../../constants/nav';
import {
  adminListCopytrade,
  adminCreateCopytrade,
  adminUpdateCopytrade,
  adminDeleteCopytrade,
  CopytradePayload,
} from '../../features/copytrade/api/copytrade';
import type { CopytradeContent } from '../../types';
import { COPYTRADE } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

interface FormState extends CopytradePayload {
  steps: string[];
}
const empty: FormState = {
  title: '', short_description: '', long_description: '', steps: [''], risk_disclaimer: '',
  embed_video_url: '', cta_label: '', cta_link: '', is_published: false,
};

export default function AdminCopytradePage() {
  const [items, setItems] = useState<CopytradeContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<CopytradeContent | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<CopytradeContent | null>(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = () => { setLoading(true); adminListCopytrade().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (c: CopytradeContent) => {
    setEditing(c);
    setForm({
      title: c.title, short_description: c.short_description, long_description: c.long_description,
      steps: c.steps.length ? c.steps : [''], risk_disclaimer: c.risk_disclaimer,
      embed_video_url: c.embed_video_url, cta_label: c.cta_label, cta_link: c.cta_link, is_published: c.is_published,
    });
    setDialog(true);
  };

  const setStep = (i: number, v: string) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => (idx === i ? v : s)) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, ''] }));
  const removeStep = (i: number) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    const payload: CopytradePayload = { ...form, steps: form.steps.map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await adminUpdateCopytrade(editing.id, payload);
      else await adminCreateCopytrade(payload);
      toast.success('Konten tersimpan');
      setDialog(false); load();
    } catch (e: any) { toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const togglePublish = async (c: CopytradeContent) => {
    try { await adminUpdateCopytrade(c.id, { is_published: !c.is_published }); toast.success(c.is_published ? 'Di-unpublish' : 'Dipublikasikan (konten lain otomatis unpublish)'); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
  };

  const del = (c: CopytradeContent) => setConfirmDel(c);
  const doDelete = async () => {
    if (!confirmDel) return;
    setDelLoading(true);
    try { await adminDeleteCopytrade(confirmDel.id); toast.success('Konten dihapus'); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setDelLoading(false); setConfirmDel(null); }
  };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Join Copytrade">
      <div className="max-w-4xl" data-testid={COPYTRADE.adminPage}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><Copy className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Content CMS</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Join Copytrade</h2>
            </div>
          </div>
          <button data-testid={COPYTRADE.adminNewBtn} onClick={openNew} className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 flex items-center gap-2"><Plus className="w-4 h-4" strokeWidth={2} /> Konten Baru</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : items.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada konten. Buat satu lalu publish.</p>
        ) : (
          <div className="space-y-2">
            {items.map((c) => (
              <div key={c.id} data-testid={COPYTRADE.adminRow} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{c.title}</p>
                    {c.is_published
                      ? <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">Published</span>
                      : <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">Draft</span>}
                  </div>
                  <p className="text-xs text-white/40 truncate">{c.short_description || `${c.steps.length} langkah`}</p>
                </div>
                <button data-testid={COPYTRADE.publishToggle} onClick={() => togglePublish(c)} title={c.is_published ? 'Unpublish' : 'Publish'} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                  {c.is_published ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <button onClick={() => openEdit(c)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                <button data-testid="copytrade-delete-button" onClick={() => del(c)} title="Hapus" className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Konten' : 'Konten Baru'}</DialogTitle>
            <DialogDescription className="text-white/50">Kelola konten halaman Join Copytrade. Publish otomatis meng-unpublish konten lain.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className={labelClass}>Judul</label><input className={inputClass} data-testid="copytrade-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label className={labelClass}>Deskripsi Singkat</label><input className={inputClass} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
            <div><label className={labelClass}>Deskripsi Lengkap</label><textarea className={inputClass} rows={3} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></div>
            <div>
              <label className={labelClass}>Langkah-langkah</label>
              <div className="space-y-2">
                {form.steps.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input className={inputClass} value={s} onChange={(e) => setStep(i, e.target.value)} placeholder={`Langkah ${i + 1}`} />
                    <button type="button" onClick={() => removeStep(i)} className="p-2 rounded-lg text-white/50 hover:text-red-400 hover:bg-white/5 shrink-0"><X className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                ))}
                <button type="button" onClick={addStep} className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" strokeWidth={2} /> Tambah langkah</button>
              </div>
            </div>
            <div><label className={labelClass}>Risk Disclaimer</label><textarea className={inputClass} rows={2} value={form.risk_disclaimer} onChange={(e) => setForm({ ...form, risk_disclaimer: e.target.value })} /></div>
            <div><label className={labelClass}>Embed Video URL</label><input className={inputClass} value={form.embed_video_url} onChange={(e) => setForm({ ...form, embed_video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>CTA Label</label><input className={inputClass} value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} /></div>
              <div><label className={labelClass}>CTA Link</label><input className={inputClass} value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Publish konten ini
            </label>
          </div>
          <DialogFooter>
            <button data-testid={COPYTRADE.adminSaveBtn} onClick={save} disabled={saving || !form.title} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!confirmDel}
        title="Hapus konten?"
        description={confirmDel ? `"${confirmDel.title}" akan dihapus permanen.` : ''}
        loading={delLoading}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </DashboardLayout>
  );
}
